import get from 'lodash/get'

import { sortByProperty } from '@core/helpers'
import { fetchPaymentMethods as fetchPaymentMethodsRequest } from '@http/endpoints'
import { getPlugin as getTranslationPlugin } from '@i18n/utils'
import logger from '@logger'

import {
  PAYMENT_GROUPS,
  PAYMENT_ICON_PATHS,
  PAYMENT_METHODS,
  PAYMENT_NETWORKS,
} from '../config/constants'
import {
  PAYMENT_GROUP_CONFIGS,
  PAYMENT_GROUP_DYNAMIC_CONFIGS,
  PAYMENT_METHOD_CONFIGS,
  PAYMENT_NETWORK_CONFIGS,
} from '../config/methods'

/**
 * @param {import('@payment').PaymentMethod} paymentMethod
 * @param {Object} options
 * @param {string} [options.forListing] - Get enabled status for a specific listing.
 * @returns {boolean} `true` if the payment method is enabled, `false` otherwise.
 */
export function isPaymentMethodEnabled(
  paymentMethod,
  { forListing = null } = {},
) {
  return forListing
    ? Boolean(paymentMethod.enabledListings?.includes(forListing))
    : paymentMethod.enabled !== false
}

/**
 * @param {import('@payment').PaymentMethod} paymentMethod - Payment method.
 * @returns {boolean} `true` if the payment method is one of Klarna's, `false` otherwise.
 */
export function isKlarna(paymentMethod) {
  return [
    PAYMENT_GROUPS.KLARNA_SLICE_IT,
    PAYMENT_GROUPS.KLARNA_PAY_LATER,
  ].includes(paymentMethod.group)
}

/**
 * @param {import('@payment').PaymentMethod} paymentMethod - Payment method.
 * @returns {boolean} `true` if the payment method is one of Oney's, `false` otherwise.
 *
 * @deprecated Use `payment/advertising` module helpers instead.
 */
export function isOney(paymentMethod) {
  return paymentMethod.group === PAYMENT_GROUPS.ONEY
}

/**
 * @param {import('@payment').PaymentMethod} paymentMethod - Payment method.
 * @returns {boolean} `true` if the payment method is debit/credit card, `false` otherwise.
 */
export function isCreditCard(paymentMethod) {
  return paymentMethod.group === PAYMENT_GROUPS.CARD
}

/**
 *
 * @param {import('@payment').PaymentMethod} paymentMethod
 * @param {Object} options
 * @param {string} [options.forListing] - Get errors for a specific listing.
 * @returns {string[]} List of errors for the payment method.
 */
export function getPaymentMethodErrors(
  paymentMethod,
  { forListing = null } = {},
) {
  return forListing
    ? paymentMethod.errorsByListings?.[forListing] || []
    : paymentMethod.errors || []
}

/**
 * @param {string} paymentMethodId
 * @returns {import('../config/methods').PaymentMethodConfig}
 */
function paymentMethodConfigFallback(paymentMethodId) {
  const name = {
    id: `Missing payment method config for "${paymentMethodId}"`,
    defaultMessage: paymentMethodId.toUpperCase(),
  }

  return { name, label: name }
}

/**
 * @param {string} paymentMethodId
 * @returns {import('../config/methods').PaymentMethodConfig}
 */
export function paymentMethodConfig(paymentMethodId) {
  return (
    PAYMENT_METHOD_CONFIGS[paymentMethodId] ||
    paymentMethodConfigFallback(paymentMethodId)
  )
}

/**
 * @param {string} paymentMethodId
 * @returns {string} The Payment Method's name
 */
export function paymentMethodName(paymentMethodId) {
  const $t = getTranslationPlugin()
  const { name, ...config } = paymentMethodConfig(paymentMethodId)

  return $t(name, config)
}

/**
 * @param {string} paymentMethodId
 * @returns {string} The Payment Method's name
 */
export function paymentMethodLabel(paymentMethodId) {
  const $t = getTranslationPlugin()
  const { label, ...config } = paymentMethodConfig(paymentMethodId)

  return $t(label, config)
}

/**
 * @param {string} paymentMethodId
 * @returns {string|null} The Payment Method's icon, if any.
 */
export function paymentMethodIcon(paymentMethodId) {
  if (!Object.values(PAYMENT_METHODS).includes(paymentMethodId)) {
    logger.error('Unknown payment method, please update PAYMENT_METHODS.', {
      paymentMethodId,
    })

    return null
  }

  return `${PAYMENT_ICON_PATHS.METHODS}/${paymentMethodId}.svg`
}

/**
 * @param {string} paymentMethodId
 * @returns {number}
 */
export function paymentMethodInstallmentCount(paymentMethodId) {
  return paymentMethodConfig(paymentMethodId).installmentCount || 0
}

/**
 * @param {string} paymentGroupId
 * @returns {import('../config/methods').PaymentGroupConfig}
 */
function paymentGroupConfigFallback(paymentGroupId) {
  const providerName = paymentGroupId.toUpperCase()

  return {
    hasCustomIcon: false,
    label: {
      id: `Missing payment group config for "${paymentGroupId}"`,
      defaultMessage: providerName,
    },
    providerName,
    messages: [],
  }
}

/**
 * @param {string} paymentGroupId
 * @param {import('../config/methods').PaymentGroupDynamicConfigOptions} [options] - Options to pass to the dynamic config.
 * @returns {import('../config/methods').PaymentGroupConfig}
 */
export function paymentGroupConfig(
  paymentGroupId,
  options = {
    methods: [],
  },
) {
  return (
    PAYMENT_GROUP_DYNAMIC_CONFIGS[paymentGroupId]?.(options) ||
    PAYMENT_GROUP_CONFIGS[paymentGroupId] ||
    paymentGroupConfigFallback(paymentGroupId)
  )
}

/**
 * @param {string} paymentNetworkId
 * @returns {string} The Payment Network's label
 */
export function paymentGroupLabel(paymentNetworkId) {
  const $t = getTranslationPlugin()

  return $t(paymentGroupConfig(paymentNetworkId).label)
}

/**
 * @param {import('../config/methods').PaymentMethod} paymentGroupId
 * @returns {string|null} The Payment Group's icon
 */
export function paymentGroupIcon(group) {
  if (!Object.values(PAYMENT_GROUPS).includes(group)) {
    logger.error('Unknown payment group, please update PAYMENT_GROUPS.', {
      paymentGroup: group,
    })

    return null
  }

  return paymentGroupConfig(group).hasCustomIcon
    ? `${PAYMENT_ICON_PATHS.GROUPS}/${group}.svg`
    : null
}

/**
 * @param {string} paymentGroupId
 * @param {string} error
 * @returns {string|null} The Payment Group's errors message, if any.
 */
export function paymentMethodError(paymentGroupId, error) {
  return get(paymentGroupConfig(paymentGroupId), `errorMessages.${error}`, null)
}

/**
 * @param {string} paymentNetworkId
 * @returns {import('../config/methods').PaymentNetworkConfig}
 */
function paymentNetworkConfigFallback(paymentNetworkId) {
  const providerName = paymentNetworkId.toUpperCase()

  return {
    name: {
      id: `Missing payment network config for "${paymentNetworkId}"`,
      defaultMessage: providerName,
    },
    icon: null,
  }
}

/**
 * @param {string} paymentNetworkId
 * @returns {import('../config/methods').PaymentNetworkConfig}
 */
function paymentNetworkConfig(paymentNetworkId) {
  return (
    PAYMENT_NETWORK_CONFIGS[paymentNetworkId] ||
    paymentNetworkConfigFallback(paymentNetworkId)
  )
}

/**
 * @param {string} paymentNetworkId
 * @returns {string} The Payment Network's label
 */
export function paymentNetworkLabel(paymentNetworkId) {
  const $t = getTranslationPlugin()

  return $t(paymentNetworkConfig(paymentNetworkId).name)
}

/**
 * @param {string} paymentNetworkId
 * @returns {string|null} Static path to the Payment Network's icon
 */
export function paymentNetworkIcon(paymentNetworkId) {
  if (!Object.values(PAYMENT_NETWORKS).includes(paymentNetworkId)) {
    logger.error('Unknown payment network, please update PAYMENT_NETWORKS.', {
      paymentNetworkId,
    })

    return null
  }

  // Re-use payment method icons when a method with the same id exists. This is
  // the case for most payment networks, except: Card ones, `klarna` & `oney`.
  return Object.values(PAYMENT_METHODS).includes(paymentNetworkId)
    ? `${PAYMENT_ICON_PATHS.METHODS}/${paymentNetworkId}.svg`
    : `${PAYMENT_ICON_PATHS.NETWORKS}/${paymentNetworkId}.svg`
}

/**
 * @param {import('../config/methods').PaymentGroupConfig[]} paymentGroups
 * @returns {import('../config/methods').PaymentGroupConfig[]} `paymentGroups`,
 * sorted by order of declaration in `PAYMENT_GROUP_CONFIGS`.
 */
export function sortedPaymentGroups(paymentGroups) {
  return sortByProperty(paymentGroups, 'id', Object.keys(PAYMENT_GROUP_CONFIGS))
}

/**
 * @param {import('@payment').PaymentMethod} paymentMethod
 * @returns {Promise<boolean>}
 */
async function isPaymentMethodAvailable(paymentMethod) {
  const isAvailableInBrowser =
    PAYMENT_METHOD_CONFIGS[paymentMethod.bmCode]?.isAvailableInBrowser ||
    (() => true)

  return process.server || isAvailableInBrowser(paymentMethod.config || {})
}

/**
 * Filter a list of payment methods, and retain only those that are available
 * in the user's browser. It relies on `isAvailableInBrowser` from
 * `PAYMENT_METHOD_CONFIGS`, or consider it is always available if not defined.
 * Note that it will return the same list on server-side.
 * @param {import('@payment').PaymentMethod[]} paymentMethods
 * @returns {Promise<import('@payment').PaymentMethod[]>}
 */
export async function filterAvailablePaymentMethods(paymentMethods) {
  const availability = await Promise.all(
    paymentMethods.map(isPaymentMethodAvailable),
  )

  return paymentMethods.filter((_, i) => availability[i])
}

/**
 * Filter a list of payment methods by removing those disabled for AB testing (remove Klarna or Affirm)
 * [PAYIN-2151]
 * @param {*} experiments
 * @param {Array<import('@payment').PaymentMethod>} paymentMethods
 */
export function filterABTestedPaymentMethods(experiments, paymentMethods) {
  switch (experiments?.bnplPaymentMethods) {
    case 'affirm':
      return paymentMethods?.filter(
        ({ bmCode }) =>
          ![
            PAYMENT_METHODS.KLARNA_PAY_LATER,
            PAYMENT_METHODS.KLARNA_SLICE_IT,
          ].includes(bmCode),
      )
    case 'klarna':
      return paymentMethods?.filter(
        ({ bmCode }) => bmCode !== PAYMENT_METHODS.AFFIRM,
      )
    default:
      return paymentMethods
  }
}

/**
 * Filter the paymentMethods property of each grade to remove methods that are disabled for AB testing.
 * @param {*} experiments
 * @param {Array<Object>} grades
 * @returns
 */
export function filterGradesABTestedPaymentMethods(experiments, grades) {
  return grades.map((grade) => {
    return {
      ...grade,
      paymentMethods: filterABTestedPaymentMethods(
        experiments,
        grade.paymentMethods,
      ),
    }
  })
}

/**
 * @param {import('vuex').ActionContext<State, Object>} context The Vuex context object.
 * @param {Object} [options] - An object containing optional parameters.
 * @param {Array} [options.listings] - An array of listing IDs.
 * @param {number} [options.bagPrice] - The price of a bag.
 * @param {boolean} [options.hasInsurance] - A flag indicating whether insurance is included.
 * @return {Promise<import('@payment').PaymentMethod[]>} The list of payment methods.
 */
export async function fetchPaymentMethods(context, options = {}) {
  const { dispatch, rootGetters } = context
  const { listings, bagPrice, hasInsurance } = options

  const { payload } = await dispatch(
    'http/request',
    {
      request: fetchPaymentMethodsRequest,
      queryParams: {
        country_code: rootGetters['config/country'],
        listings: listings?.join(','),
        bag_price: bagPrice,
        insurance_subscription: hasInsurance,
      },
    },
    { root: true },
  )

  return payload.results
}

/**
 * Sort the payment networks by putting the preferred ones first.
 * @param {string[]} networksIds
 * @param {string[]} preferredNetworksIds
 */
export function sortPaymentNetworkByPreference(
  networksIds,
  preferredNetworksIds = null,
) {
  if (!preferredNetworksIds) {
    return networksIds
  }

  return [...(networksIds || [])].sort((brandA) =>
    preferredNetworksIds.includes(brandA) ? -1 : 1,
  )
}
